import { css } from 'aphrodite';
import * as React from 'react';
import { Avatar } from '@HoudiniUI/Avatar';
import { Button } from '../../../../web/components-v2/Button';
import { composerStyles as styles } from './composerStyles';
import type { SuggestionMenuItem, SuggestionMenuState } from './types';

interface ComposerSuggestionMenuProps {
	menu: SuggestionMenuState;
	items: SuggestionMenuItem[];
	onPick: (index: number) => void;
}

const keepEditorFocus = (event: React.MouseEvent) => event.preventDefault();

/**
 * The caret-anchored popup for `/` saved replies and `@` mentions. Purely presentational — the
 * suggestion controller owns the item list, the highlighted index and the insertion.
 */
export const ComposerSuggestionMenu = React.forwardRef<HTMLDivElement, ComposerSuggestionMenuProps>(
	({ menu, items, onPick }, ref) => {
		const listRef = React.useRef<HTMLDivElement>(null);

		React.useEffect(() => {
			const active = listRef.current?.children[menu.index];
			active?.scrollIntoView?.({ block: 'nearest' });
		}, [menu.index]);

		const isSlash = menu.kind === 'slash';
		return (
			<div
				ref={ref}
				className={css(styles.suggestionMenu, !isSlash && styles.suggestionMenuMention)}
				style={{ left: menu.x, top: menu.y }}
			>
				{isSlash && (
					<div className={css(styles.suggestionMenuHeader)}>
						Saved replies
						{menu.query ? ` — "${menu.query}"` : ''}
					</div>
				)}
				<div ref={listRef} className={css(styles.suggestionMenuList, !isSlash && styles.suggestionMenuListMention)}>
					{items.length === 0 ? (
						<div className={css(styles.suggestionMenuEmpty)}>
							{isSlash ? `No saved replies match "${menu.query}"` : `No teammates match "${menu.query}"`}
						</div>
					) : (
						items.map((item, index) => (
							<Button
								key={item.key}
								type='button'
								variant='ghost'
								size='sm'
								styles={[
									styles.suggestionItem,
									...(isSlash ? [] : [styles.suggestionItemMention]),
									...(index === menu.index ? [styles.suggestionItemActive] : []),
								]}
								onMouseDown={keepEditorFocus}
								onClick={() => onPick(index)}
							>
								{item.initials != null && (
									<Avatar
										imgSrc={item.avatarImgSrc}
										altText={item.name}
										fallbackText={item.initials}
										smallText
										styles={[styles.mentionAvatar]}
									/>
								)}
								<span className={css(styles.mentionText)}>
									<span className={css(styles.suggestionItemName)}>{item.name}</span>
									{item.sub && <span className={css(styles.suggestionItemSub)}>{item.sub}</span>}
								</span>
							</Button>
						))
					)}
				</div>
				{isSlash && (
					<div className={css(styles.suggestionMenuFooter)}>↑↓ · ↵ insert · esc dismiss · variables auto-fill</div>
				)}
			</div>
		);
	}
);

ComposerSuggestionMenu.displayName = 'ComposerSuggestionMenu';
